// Đánh giá khi đăng xuất (docs/KE_HOACH_DANH_GIA_NEN_TANG.md): nút Đăng xuất gọi askLogoutSurvey thay vì logout thẳng.
// Hỏi máy chủ có bộ câu hỏi cần hỏi không (GET /survey/logout) — có thì mở hộp (LogoutSurveyHost trong AppShell), không /
// lỗi / chậm quá SURVEY_WAIT_MS thì đăng xuất luôn: không bao giờ giữ người dùng lại vì đánh giá.
//   · Gửi / Bỏ qua → ghi (POST /survey/logout) rồi đăng xuất, ghi lỗi cũng vẫn đăng xuất.
//   · Đóng hộp (Esc, chạm ra ngoài, nút X) = không đăng xuất nữa, không ghi gì.

import { useSyncExternalStore } from 'react'
import type { SurveyAnswers, SurveyDevice, SurveyQuestion } from '@shoptrack/common/platform-survey'
import { get, post } from '@/lib/api'

export const SURVEY_WAIT_MS = 2500

export interface LogoutSurvey {
  id: string
  title: string
  questions: SurveyQuestion[]
}

interface Pending {
  survey: LogoutSurvey
  context: 'unit' | 'npp'
  device: SurveyDevice
  proceed: () => Promise<void> | void
}

let pending: Pending | null = null
let asking = false
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())

function setPending(p: Pending | null) {
  pending = p
  emit()
}

export function useLogoutSurvey(): Pending | null {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn)
      return () => {
        listeners.delete(fn)
      }
    },
    () => pending,
    () => null,
  )
}

const timeout = (ms: number) => new Promise<null>((resolve) => setTimeout(() => resolve(null), ms))

/** Bấm Đăng xuất: có bộ câu hỏi thì mở hộp, không thì chạy `proceed` (đăng xuất) ngay. Bấm lặp khi đang hỏi bị bỏ. */
export async function askLogoutSurvey(opts: Omit<Pending, 'survey'>): Promise<void> {
  if (asking || pending) return
  asking = true
  let survey: LogoutSurvey | null = null
  try {
    const r = await Promise.race([get<{ survey: LogoutSurvey | null }>('/survey/logout'), timeout(SURVEY_WAIT_MS)])
    survey = r?.survey && r.survey.questions?.length ? r.survey : null
  } catch {
    survey = null
  } finally {
    asking = false
  }
  if (!survey) {
    await opts.proceed()
    return
  }
  setPending({ ...opts, survey })
}

/** Gửi câu trả lời (`answers`) hoặc Bỏ qua (null) rồi đăng xuất. */
export async function finishLogoutSurvey(answers: SurveyAnswers | null): Promise<void> {
  const p = pending
  if (!p) return
  try {
    await Promise.race([
      post('/survey/logout', { surveyId: p.survey.id, skipped: answers === null, ...(answers ? { answers } : {}), device: p.device, context: p.context }),
      timeout(SURVEY_WAIT_MS * 2),
    ])
  } catch {
    /* ghi lỗi vẫn đăng xuất — đánh giá không được giữ người dùng lại */
  }
  setPending(null)
  await p.proceed()
}

/** Đóng hộp mà không đăng xuất. */
export function cancelLogoutSurvey(): void {
  setPending(null)
}
